<template>
  <div class="Evaluation-modal-overlay">
    <transition name="modal-expand">
      <div v-if="isVisible" class="Evaluation-modal" @click.stop>
        <div class="modal-content">
          <div class="title-container">
            <font-awesome-icon class="window-close-icon" :icon="['fas', 'window-close']" @click="close" />
            <h2>Assign Trainees to {{ $labels.project }} ID: {{
              this.selectedProjectId
              }}</h2>
          </div>
          <div class="flex-grow-1 d-flex justify-content-center">
            <div v-if="isDisabled" class="expired-box">
              This {{ $labels.project }} has expired. Trainees can no longer be assigned.
            </div>
          </div>
          <div class="container-fluid">
            <vue-good-table :columns="columns" :rows="filteredTrainees"
              :pagination-options="{ enabled: true, perPage: 10 }" :search-options="{ enabled: true }"
              :sort-options="{ enabled: true }" styleClass="vgt-table condensed">
              <template #table-row="props">
              <!-- Assign Checkbox -->
                <template v-if="props.column.field === 'select'">
                  <div @click="toggleCheckbox(props.row.user_id)">
                    <input type="checkbox" v-model="selectedTrainees" :value="props.row.user_id"
                    :disabled="alreadyAssignedTrainees.includes(props.row.user_id) || isDisabled" @click.stop>
                  </div>
                </template>
                <!-- Remove Trash Icon -->
                <span v-else-if="props.column.field === 'remove'">
                  <font-awesome-icon icon="trash-alt" class="delete-icon"
                    v-if="alreadyAssignedTrainees.includes(props.row.user_id)"
                    :class="{ 'disabled-icon': isDisabled }" @click="removeTrainee(props.row.user_id)" />
                </span>
                <!-- Default -->
                <span v-else :title="String(props.formattedRow[props.column.field] ?? '')">
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </div>
          <div class="button-container">
            <button class="ins-close-button" @click="close()">Close</button>
            <button class="ins-save-button" @click="assignSelectedTrainees" :disabled="isDisabled">Assign
              Trainees</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { VueGoodTable } from 'vue-good-table-next';
import 'vue-good-table-next/dist/vue-good-table-next.css';

export default {
  name: 'AssignTraineesModal',
  components: {
    VueGoodTable,
  },
  props: {
    selectedProjectId: {
      type: Number,
      required: true,
    },
    status: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      trainees: [],
      selectedTrainees: [],
      columns: [
        { label: 'ID', field: 'user_id', sortable: true, thClass: 'vgt-id-column', tdClass: 'vgt-id-column' },
        { label: 'First Name', field: 'first_name', sortable: true, thClass: 'vgt-first-name-column', tdClass: 'vgt-first-name-column' },
        { label: 'Last Name', field: 'last_name', sortable: true, thClass: 'vgt-last-name-column', tdClass: 'vgt-last-name-column' },
        { label: 'Email', field: 'email', sortable: true, thClass: 'vgt-email-column', tdClass: 'vgt-email-column' },
        { label: 'Select Trainee', field: 'select', sortable: false, type: 'html', thClass: 'vgt-actions-column', tdClass: 'vgt-actions-column' },
        { label: 'Remove Trainee', field: 'remove', sortable: false, type: 'html', thClass: 'vgt-actions-column', tdClass: 'vgt-actions-column' },
      ],
      alreadyAssignedTrainees: [],
      isVisible: false,
    };
  },
  computed: {
    filteredTrainees() {
      return this.trainees;
    },
    isDisabled() {
      return this.status === 1;
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    toggleCheckbox(userId) {
      if (this.alreadyAssignedTrainees.includes(userId)) return;

      const index = this.selectedTrainees.indexOf(userId);
      if (index > -1) {
        this.selectedTrainees.splice(index, 1);
      } else {
        this.selectedTrainees.push(userId);
      }
    },
    async assignSelectedTrainees() {
      if (this.selectedTrainees.length === 0) {
        return;
      }

      const project_id = this.selectedProjectId;
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const existingTrainees = this.alreadyAssignedTrainees;

      // Filter out Trainees already assigned to the project
      const newTrainees = this.selectedTrainees.filter(
        traineeId => !existingTrainees.includes(traineeId)
      );

      console.log('New trainees to assign:', newTrainees);

      if (newTrainees.length === 0) {
        window.alert(`All selected trainees are already in the ${this.$labels.project}.`);
        this.selectedTrainees = [];
        return;
      }

      try {
        // Step 1: Assign new Trainees to the project
        const projectData = {
          user_ids: newTrainees,
          role: 'trainee',
        };

        const addUsersResponse = await axios.post(
          `/api/projects/${project_id}/add-users`,
          projectData,
          { headers }
        );
        console.log('Trainees added to the Project:', addUsersResponse);

        window.alert(`Trainees assigned successfully to the ${this.$labels.project}.`);
        this.selectedTrainees = [];
        this.$emit('close');
      } catch (error) {
        console.error('Error assigning Trainees:', error);
        if (error.isOffline === true) {
          this.$emit('close');
          return;
        }
        window.alert('An error occurred while assigning trainees. Please try again.');
      }
    },
    async removeTrainee(user_id) {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // You may want to show a confirmation dialog before removing the trainee
        const confirmRemove = confirm(`Are you sure you want to remove this trainee from the ${this.$labels.project}?`);

        if (!confirmRemove) {
          return;
        }

        // Send a DELETE request to your API to remove the trainee from the Project
        const response = await axios.delete(`/api/users/${user_id}/projects/${this.selectedProjectId}/roles/trainee`, { headers });

        if (response.status === 200) {
          window.alert(`Trainee successfully removed from ${this.$labels.project}.`);
          this.loadData()
        } else {
          // Handle errors here
          console.error('An error occurred while removing the trainee:', response);
        }
      } catch (error) {
        console.error('An error occurred while removing the trainee:', error);
        if (error.isOffline === true) {
          this.$emit('close');
          return;
        }
      }
    },
    async loadData() {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch the trainees assigned to the project
        const response = await axios.get(`/api/projects/${this.selectedProjectId}/users`, { headers });
        const assignedTrainees = response.data.filter(user => user.pivot.role === 'trainee');
        this.alreadyAssignedTrainees = assignedTrainees.map(trainee => trainee.user_id);

        // Now, you have the assigned trainees in the 'assignedtrainees' variable.
        console.log(assignedTrainees);

        // Fetch all trainees (including those assigned to the project)
        const result = await axios.get('/api/users', { headers });

        if (result.status === 200) {
          // Filter the response data to include only users with the role 'trainee'
          this.trainees = result.data.filter(user => Array.isArray(user.roles) && user.roles.includes('trainee'));

          this.selectedTrainees = [...this.alreadyAssignedTrainees];
        }

      } catch (error) {
        console.error('An error occurred while fetching data:', error);
      }
    }
  },
  async mounted() {
    this.isVisible = true;
    this.loadData();
  },
};
</script>

<style scoped>
.modal-content h2 {
    font-size: 30px;
    margin-left: 20px;
}

.Evaluation-modal-overlay {
  position: fixed;
  top: 0px;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: top;
  width: 100%;
  height: 100vh;
  z-index: 1;
}

.Evaluation-modal {
  background-color: #ffffff;
  overflow-y: auto;
  /* Enables vertical scrolling if needed */
  max-width: 90vw;
  max-height: 95vh;
  margin-top: 20px;
  min-width: max(65vw, 1100px);
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  position: fixed;
  display: flex;
  flex-direction: column;
  overflow-wrap: break-word;
  padding: 20px;
  z-index: 1002;
}

.button-container {
  display: flex;
  justify-content: space-between;
  /* Aligns items to the right */
  align-items: center;
  margin-top: 0px;
}

.ins-save-button {
  padding: 10px 10px;
  font-size: 16px;
  width: 180px;
  height: 40px;
  margin-top: 0px;
  margin-right: 20px;
  border: 1px solid rgb(0, 96, 134);
  border-radius: 5px;
  background: #2952c4;
  color: white;
  cursor: pointer;
  text-align: center;
  transition: transform 0.2s, box-shadow 0.2s;
}

.ins-close-button {
  background-color: #6c757d;
  border: 1px solid #4d5358;
  margin-top: 0px;
  color: white;
  padding: 5px 20px;
  border-radius: 5px;
  margin-left: 20px;
}

.ins-close-button:hover {
  background-color: #4d5358;
}

.ins-save-button:disabled {
  background-color: rgb(216, 216, 216);
  cursor: not-allowed;
  color: grey
}

.ins-save-button:disabled:hover {
  background-color: rgb(216, 216, 216);
  color: grey
}

.delete-icon {
  font-size: 16px;
  color: #FF0000;
  /* Red color */
  cursor: pointer;
  transition: color 0.2s, transform 0.2s, box-shadow 0.2s;
}

.delete-icon:hover {
  color: #a82222;
  /* Lighter red color on hover */
  cursor: pointer;
  transform: scale(1.1);
  /* Scale the icon on hover */
  /* You can also add a box-shadow or any other styles you prefer */
}

.modal-content {
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  padding: 15px;
}

.delete-icon.disabled-icon {
  pointer-events: none;
  cursor: not-allowed;
  opacity: 0.5;
  filter: grayscale(100%);
}

.title-container {
  margin-top: 10px;
  margin-bottom: 15px;
}
</style>